import { track } from './openpanel';

export type ErrorCategory = 'api' | 'network' | 'auth' | 'validation' | 'ui' | 'unknown';
export type ErrorSource = 'apiClient' | 'errorBoundary' | 'trycatch' | 'service';

export interface TrackErrorInput {
  source: ErrorSource;
  category: ErrorCategory;
  error: unknown;
  context?: Record<string, unknown>;
}

const lastSeen = new Map<string, number>();

export function sanitizeText(input: unknown, maxLen = 2000): string {
  const text = String(input ?? '');
  let out = text;

  // emails
  out = out.replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[REDACTED]');
  // jwt (rough)
  out = out.replace(
    /eyJ[a-zA-Z0-9_-]{10,}\.[a-zA-Z0-9_-]{10,}\.[a-zA-Z0-9_-]{10,}/g,
    '[REDACTED]'
  );
  // bearer tokens
  out = out.replace(/Bearer\s+[A-Za-z0-9._-]+/gi, 'Bearer [REDACTED]');
  // uuid
  out = out.replace(
    /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi,
    '[REDACTED]'
  );
  // common secrets in query / key-value
  out = out.replace(/\b(token|password|code|secret|authorization)=([^&\s]+)/gi, '$1=[REDACTED]');

  if (out.length > maxLen) return out.slice(0, maxLen);
  return out;
}

export function createFingerprint(args: {
  category: ErrorCategory;
  code?: string;
  path?: string;
  topStack?: string;
}): string {
  return [args.category, args.code ?? '', args.path ?? '', args.topStack ?? ''].join('|');
}

export function shouldDedupe(fingerprint: string, windowMs: number): boolean {
  const now = Date.now();
  const prev = lastSeen.get(fingerprint);
  if (prev && now - prev < windowMs) return true;
  lastSeen.set(fingerprint, now);
  return false;
}

function extractErrorBasics(error: unknown): { message?: string; stack?: string; name?: string } {
  if (error instanceof Error) {
    return { message: error.message, stack: error.stack, name: error.name };
  }
  if (typeof error === 'object' && error && 'message' in error) {
    return { message: String((error as any).message) };
  }
  return { message: typeof error === 'string' ? error : undefined };
}

function safeString(value: unknown): string | undefined {
  if (value == null) return undefined;
  const s = String(value);
  return s.length ? s : undefined;
}

export function trackError({ source, category, error, context }: TrackErrorInput): void {
  const basics = extractErrorBasics(error);
  const path = typeof window !== 'undefined' ? window.location.pathname : undefined;
  const search = typeof window !== 'undefined' ? window.location.search : undefined;

  const topStack = sanitizeText(basics.stack ?? '').split('\n')[0] ?? '';
  const code =
    typeof error === 'object' && error && 'code' in error ? safeString((error as any).code) : undefined;
  const status =
    typeof error === 'object' && error && 'status' in error
      ? Number((error as any).status)
      : undefined;

  const fingerprint = createFingerprint({ category, code, path, topStack });
  if (shouldDedupe(fingerprint, 10_000)) return;

  track('error', {
    category,
    source,
    code,
    status,
    name: safeString(basics.name),
    message: sanitizeText(basics.message, 300),
    stack: sanitizeText(basics.stack, 4000),
    route: {
      path,
      search: sanitizeText(search, 500),
    },
    fingerprint,
    ...context,
  });
}

